/**
 * Feature: Estimating & Measurements
 * /features/estimating
 */
import {
  FeatureFaq,
  FeatureStats,
  FeatureSignup,
  FeatureHeroBadge,
  FeatureChecklist,
} from '@/components/marketing/FeaturePageSections';

import { Button } from '@/components/ui/button';
import { SEOHead } from '@/components/seo/SEOHead';
import { Breadcrumbs } from '@/components/seo/Breadcrumbs';
import { Link } from 'react-router-dom';
import { getAuthLink } from '@/lib/links';
import { softwareApplicationSchema, faqPageSchema } from '@/lib/schemas';
import { Helmet } from 'react-helmet-async';
import { Globe, Hash, DollarSign, Package, FileText, TrendingUp, ArrowRight } from 'lucide-react';
import { ProductShowcase } from '@/components/marketing/ProductShowcase';
import { PRODUCT_FRAMES } from '@/lib/productFrames';
import { CapabilityLedger } from '@/components/marketing/CapabilityLedger';

const faqs = [
  {
    question: 'How accurate are satellite measurements?',
    answer:
      'Within ±2% of manual measurement for standard residential roofs. Results can vary on complex roofs with heavy tree cover. We recommend an on-site verification for insurance supplement work.',
  },
  {
    question: 'Can I add custom line items to estimates?',
    answer:
      'Yes. Add any line item — labor, specialty materials, disposal fees, or miscellaneous costs. Save custom items to your catalog for reuse.',
  },
  {
    question: 'Does Rooflyzer integrate with EagleView or other measurement services?',
    answer:
      'Rooflyzer includes built-in SmartRoof satellite measurements. Third-party integrations with EagleView and GAF QuickMeasure are on our integration roadmap.',
  },
  {
    question: 'Can I set margin and markup automatically?',
    answer:
      'Yes. Set target margin or markup percentages and Rooflyzer applies them automatically to material and labor costs.',
  },
  {
    question: 'Can customers request a self-serve estimate?',
    answer:
      'Yes. Embed the Instant Estimate Widget on your website so homeowners can get a ballpark price without you having to send a rep.',
  },
];

const estimatingStats = [
  {
    value: 'Address → total',
    label: 'One sitting',
    desc: 'Satellite measurement to priced estimate in the app',
  },
  {
    value: '±2%',
    label: 'Measurement target',
    desc: 'Design goal for satellite takeoffs vs manual measuring',
  },
  {
    value: 'Same day',
    label: 'Delivery',
    desc: 'Digital estimates go out as soon as they are priced',
  },
  {
    value: 'Good / better / best',
    label: 'Tiered pricing mode',
    desc: 'A workspace-wide option alongside range and fixed quotes',
  },
];

export const EstimatingPage = () => {
  const appSchema = softwareApplicationSchema({
    name: 'Rooflyzer OS — Roofing Estimating & Measurements',
    description:
      'Build accurate roofing estimates in minutes with satellite measurements, material pricing, and labor calculators. Win more jobs with professional proposals every time.',
    featureList: [
      'Satellite Measurements',
      'Automated Material Calculations',
      'Live Material Pricing',
      'Saved Material Packages',
      'Instant Estimate to Proposal',
      'Estimate Win Rate Analytics',
    ],
    url: 'https://rooflyzer.pro/features/estimating',
    offers: { price: '0', description: 'Free tier available — no credit card required' },
  });

  const faqSchema = faqPageSchema(faqs);

  return (
    <>
      <SEOHead
        title="Roofing Estimating Software — Faster, More Accurate Quotes"
        description="Build accurate roofing estimates in minutes with satellite measurements, material pricing, and labor calculators. Win more jobs with professional proposals every time."
        canonicalUrl="/features/estimating"
        ogImage="/og/instant-quote.png"
        keywords="roofing estimating software, roofing estimate calculator, roofing measurement software, roof estimate tool"
      />
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(appSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Helmet>

      <div className="pt-24 min-h-screen px-4 sm:px-6 pb-20">
        <div className="max-w-6xl mx-auto">
          <Breadcrumbs
            items={[
              { name: 'Features', url: '/features' },
              { name: 'Estimating', url: '/features/estimating' },
            ]}
            className="mb-8"
          />

          {/* Hero */}
          <section className="text-center max-w-4xl mx-auto mb-20">
            <FeatureHeroBadge icon={Hash}>Accurate Every Time</FeatureHeroBadge>
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 tracking-tight">
              Roof Estimates in Minutes, <span className="text-[#C29BF8]">Not Hours</span>
            </h1>
            <p className="text-base sm:text-lg md:text-xl text-slate-300 mb-8 max-w-3xl mx-auto leading-relaxed">
              Pull satellite measurements, apply your material packages, and generate a professional
              estimate in under 5 minutes. Your customer has a price before you leave the property.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                size="lg"
                className="bg-[#7950A2] text-white hover:bg-[#68418f] font-semibold px-8 shadow-md shadow-[#7950A2]/20"
                onClick={() => window.location.assign(getAuthLink('/register'))}
              >
                Estimate Your First Roof Free
                <ArrowRight className="ml-2 size-5" />
              </Button>
              <Link to="/pricing">
                <Button
                  size="lg"
                  variant="ghost"
                  className="border border-white/20 text-white hover:bg-white/10"
                >
                  See Pricing
                </Button>
              </Link>
            </div>
          </section>

          {/* Product film */}
          <section className="mb-24">
            <ProductShowcase frame={PRODUCT_FRAMES.instant} float={false} />
          </section>

          {/* Capability Grid */}
          <section className="mb-24">
            <h2 className="text-2xl sm:text-3xl font-bold text-white text-center mb-12">
              Everything You Need to Win More Jobs
            </h2>
            <CapabilityLedger
              items={[
                {
                  icon: Globe,
                  title: 'Satellite Measurements',
                  desc: 'Pull accurate roof measurements from satellite imagery. No manual measuring required — just enter the address.',
                },
                {
                  icon: Hash,
                  title: 'Automated Material Calculations',
                  desc: 'Enter pitch, square footage, and material type. Rooflyzer calculates shingles, underlayment, flashing, and waste factor automatically.',
                },
                {
                  icon: DollarSign,
                  title: 'Live Material Pricing',
                  desc: 'Use your own supplier pricing or market averages. Update prices once and they apply to all new estimates.',
                },
                {
                  icon: Package,
                  title: 'Saved Material Packages',
                  desc: 'Create packages for your most common jobs: 3-tab, architectural, metal, TPO. Apply in one click.',
                },
                {
                  icon: FileText,
                  title: 'Instant Estimate to Proposal',
                  desc: 'Convert an approved estimate to a signed proposal with one click. No re-entry, no mistakes.',
                },
                {
                  icon: TrendingUp,
                  title: 'Estimate Win Rate Analytics',
                  desc: 'Track which estimate amounts, material types, and rep presentations win the most jobs.',
                },
              ]}
            />
          </section>

          {/* The Problem → Solution */}
          <section className="mb-24 bg-white/5 backdrop-blur-xl rounded-3xl p-8 sm:p-12 border border-white/10">
            <div className="grid md:grid-cols-2 gap-12">
              <div>
                <h2 className="text-2xl font-bold text-white mb-6">The Old Way</h2>
                <ul className="space-y-4">
                  {[
                    'Manual measurements on a ladder in all weather',
                    'Calculate materials in Excel, hope nothing is wrong',
                    'Price per supplier quote that changes weekly',
                    'Build estimate in Word, email a PDF, and wait days',
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-3 text-white/80">
                      <span className="text-red-400 mt-0.5 text-lg">✕</span>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <h2 className="text-2xl font-bold text-white mb-6">Rooflyzer Estimating</h2>
                <FeatureChecklist
                  items={[
                    'Satellite measurements from the office in seconds',
                    'Automated material calc — shingles, underlayment, waste factor',
                    'Live pricing that reflects your supplier rates',
                    'Estimate to proposal in one click — customer signs same day',
                    'Win rate analytics so you know exactly what closes',
                  ]}
                />
              </div>
            </div>
          </section>

          {/* Stats */}
          <FeatureStats title="Built for Speed and Accuracy" items={estimatingStats} />

          {/* FAQ */}
          <FeatureFaq faqs={faqs} />

          {/* CTA */}
          <FeatureSignup
            title="Start Estimating Faster Today"
            buttonLabel="Estimate Your First Roof Free"
          >
            Free to start. Satellite measurements included. Build your first estimate in under 5
            minutes.
          </FeatureSignup>
        </div>
      </div>
    </>
  );
};

export default EstimatingPage;
